/**
 * Plain-language content for the Strategies page, written for someone who has never traded.
 * English is the key; Turkish lives in lib/i18n-tr.ts (i18n.test.ts checks every string).
 */

export interface StrategyGuide {
  readonly summary: string;
  readonly steps: readonly string[];
  readonly goodFor: string;
  readonly risk: string;
  readonly tags: readonly string[];
  /** Parameters shown up front; everything else goes under "Advanced settings". */
  readonly main: readonly string[];
}

export const STRATEGY_GUIDE: Readonly<Record<string, StrategyGuide>> = {
  sentiment_first: {
    summary: 'Buys when the news about a coin is clearly good — but only if the market is actually reacting to it.',
    steps: [
      'Every minute it reads crypto news sites and Binance announcements and rates each headline as good or bad for the coin it mentions.',
      'When a coin’s news is clearly good, it checks the market: is trading suddenly busier than normal, has the price not already jumped, is Bitcoin calm, is buying cheap right now?',
      'If every check passes it buys. If trading hasn’t picked up yet it waits up to 3 minutes, then gives up.',
    ],
    goodFor: 'Days when big news moves prices. The fastest of the three to react.',
    risk: 'News can be misread or already known by everyone; the market checks exist to catch that.',
    tags: ['Uses news', 'Trades: sometimes'],
    main: ['minCompositeScore', 'minCompositeConfidence', 'maxSignalsPerHour', 'cooldownPerSymbolMin'],
  },
  hybrid_confirmation: {
    summary: 'Waits for good news AND a clear sign from the market before buying — slower, but pickier.',
    steps: [
      'Good news about a coin opens a watch window (5 minutes by default).',
      'Inside that window it waits until trading gets busier than normal and there are clearly more buyers than sellers.',
      'Only then — if the price hasn’t already run away and Bitcoin is calm — it buys. If the window ends first, nothing happens.',
    ],
    goodFor: 'Starting out: fewer trades, each one confirmed twice.',
    risk: 'Being picky means it misses some fast moves.',
    tags: ['Uses news + market', 'Trades: rarely'],
    main: ['minCompositeScore', 'confirmationWindowMs', 'minOrderbookImbalance', 'cooldownPerSymbolMin'],
  },
  technical_hunter: {
    summary: 'Ignores the news. Buys a coin that has dropped sharply at the moment its chart shows the fall turning around.',
    steps: [
      'It watches the price chart of every coin in the list.',
      'It looks for a coin that fell hard in a short time (“oversold”).',
      'When the chart shows that fall starting to turn upward, it buys — expecting a bounce.',
    ],
    goodFor: 'Markets that swing up and down without big news.',
    risk: 'In a strong downtrend a falling coin can keep falling; the exit strategy limits the loss.',
    tags: ['Uses charts only', 'Trades: often'],
    main: ['timeframe', 'rsiOversold', 'cooldownPerSymbolMin'],
  },
  trend_follower: {
    summary: 'Buys coins that are climbing to new highs and holds them for days or weeks while the rise lasts. The strategy with the strongest research behind it.',
    steps: [
      'Once a day, right after midnight (UTC), it looks at the daily chart of the most traded coins.',
      'For each coin it asks eight questions: is the price above its highest point of the last 10, 20, 30… up to 360 days? When at least half say yes, the coin is trending.',
      'It holds the 5 strongest trends. The stronger a coin’s trend, the more money it gets — coins that swing wildly get less. Nothing new is bought while Bitcoin itself is in a downtrend.',
      'Every day it resizes: it buys more of a coin whose trend got stronger and sells part of one that weakened. Changes under 25% are skipped to save fees.',
      'A coin that falls out of the top 5 or loses its trend is sold. Daily ups and downs alone never trigger a sale; only a 25% crash triggers an emergency sale.',
    ],
    goodFor: 'Long rising markets. Tested on 2022–2026 data it earned about twice as much as simply holding Bitcoin, with smaller falls.',
    risk: 'It buys late and sells late: it gives back part of the gains when a trend ends, and in sideways markets it loses small amounts repeatedly. Expect losing months.',
    tags: ['Uses charts only', 'Trades: rarely', 'Holds for days'],
    main: ['maxPositions', 'maxCoinPct', 'mode', 'btcFilter'],
  },
};

export const EXIT_GUIDE: Readonly<Record<string, string>> = {
  trailing_stop: 'Follows the price up and sells if it falls a set % below the highest point. Lets winning trades run and cuts them when the trend turns; once it protects a profit, that profit is real after fees.',
  fixed_tpsl: 'Sells at a fixed profit target or a fixed loss limit, whichever comes first — both counted after Binance fees. Simple and predictable.',
  scale_out: 'Sells part of the position at a first profit target, then follows the rest like the trailing stop.',
  trend_hold: 'Used automatically by the trend follower: the strategy itself decides when to sell. This only adds an emergency sale if the price falls a set % below the buy price.',
};

/** One-line explanation under each setting. Keys are the parameter names. */
export const PARAM_HELP: Readonly<Record<string, string>> = {
  // entry strategies
  minCompositeScore: 'How good the news must be: −1 very bad, 0 neutral, 1 very good. 0.5 means clearly good.',
  minCompositeConfidence: 'How sure the news reading must be, from 0 to 1. Higher means fewer but surer trades.',
  maxSignalsPerHour: 'At most this many buys per hour across all coins — a brake when a lot of news arrives at once.',
  cooldownPerSymbolMin: 'After buying a coin, wait this many minutes before buying the same coin again.',
  confirmationWindowMs: 'How long to wait for the market to confirm the news.',
  minOrderbookImbalance: 'Share of buyers among waiting orders: 0.6 means at least 60% buyers.',
  maxOrderbookAgeMs: 'Order-book data older than this is not trusted.',
  timeframe: 'Chart candle size: 1 minute or 5 minutes. 5 minutes is calmer.',
  rsiPeriod: 'How many candles the “oversold” meter (RSI) looks at. 14 is standard.',
  rsiOversold: 'RSI below this counts as oversold — a sharp drop. 30 is standard; lower means only very sharp drops.',
  oversoldLookbackBars: 'The drop must have happened within this many candles before the turn.',
  macdFast: 'Part of the turn detector (MACD). Standard values: 12 / 26 / 9.',
  macdSlow: 'Part of the turn detector (MACD). Standard values: 12 / 26 / 9.',
  macdSignal: 'Part of the turn detector (MACD). Standard values: 12 / 26 / 9.',
  atrPeriod: 'How many candles are used to measure a coin’s typical move.',
  volumeProfileBars: 'How many candles are used to find the price where most trading happened.',
  // trend follower
  universeSize: 'How many coins it may hold: the most traded ones, by typical daily volume.',
  minMedianVolumeUsdt: 'Skip coins that trade less than this per day (USDT). Thin markets are easy to manipulate.',
  maxCoinPct: 'Largest share of the account for one coin, in %. Coins that swing more get less. The risk page’s max position also applies.',
  targetVolPct: 'How wild a coin may be before its position is made smaller (yearly swing, in %).',
  volLookbackDays: 'How many days are used to measure how wild a coin is.',
  btcFilter: 'Do not buy anything new while Bitcoin is below its 200-day average — the whole market is usually weak then.',
  entrySignal: 'Share of the eight trend signals that must be on to buy (0.5 = half).',
  exitSignal: 'Sell when fewer than this share of the signals are still on (0.25 = a quarter).',
  maxPositions: 'At most this many coins at once. The strongest trends get the places.',
  rotation: 'When every place is taken, sell the weakest coin to buy a full-strength trend. Tested: slightly better, about 10 more trades over 4.7 years.',
  rotateMinSignal: 'How strong an outside coin must be to replace a held one (1 = all eight signals on).',
  rotateBelowSignal: 'Only a held coin weaker than this can be replaced (0.5 = fewer than half the signals on).',
  minHoldDays: 'A coin must be held at least this many days before it can be replaced — no back-and-forth.',
  mode: 'continuous: every day each position follows its trend strength (best in tests). binary: buy the full amount once, hold, sell when the trend ends.',
  rebalanceThresholdPct: 'Skip resizing when the change is smaller than this share of the position, in % — fewer small trades, lower fees.',
  // market checks (gates)
  gates: 'Checks done on the market right before every news-based buy. Each can be turned off.',
  enabled: 'Turn this check on or off.',
  windowBars: 'How many of the latest 1-minute candles are compared with normal.',
  lookbackBars: 'How many earlier 1-minute candles define “normal” trading.',
  minRvol: 'Trading must be at least this many times busier than normal (2 = twice as busy).',
  maxWaitSec: 'How long to wait after the news for trading to pick up.',
  maxMoveAtr: 'Skip if the price already moved more than this many typical moves since the news.',
  maxNewsAgeSec: 'Ignore news older than this.',
  lookbackMin: 'How many minutes of Bitcoin’s price to look at.',
  maxBtcDropPct: 'Skip if Bitcoin fell more than this % in that time.',
  maxSpreadBps: 'Skip if the gap between buy and sell prices is wider than this (100 bps = 1%).',
  maxTickerAgeMs: 'Price data older than this is treated as stale.',
  // exit strategies
  trailPct: 'Sell if the price falls this % below its highest point since buying.',
  ratchetAtrMult: 'Optional: give the stop more room in wild markets (in typical moves). Leave empty to turn off.',
  activationPct: 'Start following the price only after it is this % above the buy price (0 = right away).',
  tpPct: 'Take profit: sell when the trade is up this % after both Binance fees.',
  slPct: 'Stop loss: sell when the trade is down this %, fees included.',
  tp1Pct: 'First target: sell part of the position when the trade is up this % after fees.',
  tp1SizePct: 'How much of the position to sell at the first target, in %.',
  maxHoldMin: 'Sell after this many minutes if no stop or target was hit yet. Empty = no time limit.',
};

export const GUIDE_UI = [
  'What is a strategy?',
  'A strategy decides when the bot BUYS a coin. When to SELL is decided separately by the exit strategy below. You can run several strategies at once — for example the chart strategy next to a news strategy — and compare them on the Reports page. A coin is never bought twice: whichever strategy buys it first holds it.',
  'Right now the bot is in simulation: it trades with pretend money, so trying strategies costs nothing.',
  'The bot is LIVE: strategies trade real money.',
  'How it works',
  'Good for',
  'Watch out',
  'Main settings',
  'Advanced settings',
  'Every setting has a safe default. You only need to touch the main settings — or none at all.',
  'Exit strategies',
  'After buying, the exit strategy decides when to sell. The one marked “in use” applies to every new trade.',
  'in use',
  'Use for new trades',
  'Uses news',
  'Uses news + market',
  'Uses charts only',
  'Trades: sometimes',
  'Trades: rarely',
  'Trades: often',
  'Holds for days',
  'Active',
  'Turn on',
  'Stop this strategy',
] as const;
